/**
 * `/admin/llm` view — issue #119 (data population on the scaffolding
 * shipped by #92).
 *
 * Architecture:
 *   - The view function produces the static landmark + heading skeleton
 *     and one anchor div per panel. Each anchor's `onMount` hook fires
 *     once per route-scope mount, fetches the relevant endpoint, and
 *     populates the panel imperatively.
 *   - Reactive llui state is intentionally **NOT** used for admin data;
 *     the data is route-local (re-fetched on each navigate into
 *     `/admin/llm`), no other view consumes it, and TEA traffic would
 *     bloat the global Model for one page.
 *   - 401 → "sign in required" prompt. 404 → "not found" panel (the
 *     hard constraint from the brief — server gate must not be weakened
 *     and the client must render not-found-shaped UI when non-admin).
 *
 * The view exports `viewAdminLlm(state, send)` so the dispatcher in `app.ts` can
 * import + re-wrap as a same-module function (see CLAUDE.md "View
 * dispatcher patterns" for the 0.5.2 opaque-state-flow rule).
 *
 * Accessibility (each panel):
 *   - `role="region"` with `aria-labelledby`
 *   - status node `role="status" aria-live="polite"` for loading/empty
 *   - error node `role="alert"` for retry surfaces
 *   - form inputs have `<label for>`, errors via `aria-describedby`
 *   - icon-only buttons carry `aria-label`
 */
import { button, pageHeader, sectionHeading, tabs, textInput } from '@dl/ui';
import {
  div,
  form,
  label,
  main,
  option,
  p,
  section,
  select,
  span,
  text,
  type Mountable,
  type Send,
  type Signal,
} from '@llui/dom';

import { t } from '../i18n.js';

import {
  addKey,
  createModel,
  getAnalytics,
  getLlmSettings,
  putLlmSettings,
  setKillSwitch,
  listAudit,
  listBindings,
  listKeys,
  listModels,
  listProviders,
  listTemplateVersions,
  patchProvider,
  retireKey,
  revertTemplate,
  rotateKey,
  setBinding,
  setTemplate,
  testProviderConnection,
  listTemplates,
  parseBindingConfig,
  type AdminResult,
  type Binding,
  type BindingConfig,
  type Model,
  type PromptTemplate,
  type Provider,
  type TemplateVersion,
  type Analytics,
  type AnalyticsWindow,
  type LlmSettings,
} from './api.js';
import { onAdminMount } from './mount.js';


const TASK_KINDS = ['prose_suggest', 'trait_author', 'image_generate', 'audit', 'embed', 'name_suggest', 'reader'] as const;
const POSTURES = ['standard', 'privacy_plus', 'local'] as const;

/**
 * The admin-editable prompt-template keys (mirror of the worker's
 * `TEMPLATE_KEYS`). Drives the version-history template selector — a closed,
 * stable enum, so a code constant is fine (the editor list is data-driven, but
 * the history selector must exist before its data loads).
 */
const TEMPLATE_KEYS = [
  'prose_suggest.system',
  'prose_suggest.user',
  'draft_entity.system',
  'draft_entity.user',
  'image_generate.prompt',
] as const;

/**
 * Sentinel disposer for branches that have no resources to release.
 * Named so the lint rule doesn't flag `() => {}` literals scattered
 * through each mount function.
 */
function noopDisposer(): void {
  // no-op disposer
}

/** Format an ISO timestamp via the user's locale. */
function formatAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

/**
 * Apply a not-found / unauthenticated overlay to the panel container,
 * suppressing the per-panel mount path so the user sees one
 * authoritative not-found screen rather than 6 broken panels.
 *
 * Returns `true` when the overlay is rendered (the caller should
 * short-circuit its data-population path).
 */
function maybeRenderTopLevelOverlay(container: Element, result: AdminResult<unknown>): boolean {
  if (result.ok) return false;
  if (result.kind === 'not-found' || result.kind === 'unauthenticated') {
    const root = container.closest('[data-admin-root]');
    if (!root) return false;
    if (root.querySelector('[data-overlay-rendered]')) return true;
    const overlay = document.createElement('div');
    overlay.setAttribute('data-overlay-rendered', '');
    overlay.setAttribute('class', 'admin-llm-overlay');
    overlay.setAttribute('role', 'alert');
    overlay.setAttribute('data-testid', 'admin-llm-overlay');

    const heading = document.createElement('h2');
    const body = document.createElement('p');
    if (result.kind === 'not-found') {
      heading.textContent = t('admin.llm.not_found.heading');
      body.textContent = t('admin.llm.not_found.body');
      overlay.setAttribute('data-overlay-kind', 'not-found');
    } else {
      heading.textContent = t('admin.llm.unauthenticated.heading');
      body.textContent = t('admin.llm.unauthenticated.body');
      overlay.setAttribute('data-overlay-kind', 'unauthenticated');
      const a = document.createElement('a');
      a.setAttribute('href', '/sign-in');
      a.setAttribute('class', 'admin-llm-overlay-link');
      a.setAttribute('data-testid', 'admin-llm-sign-in-link');
      a.textContent = t('admin.llm.unauthenticated.link');
      body.appendChild(document.createTextNode(' '));
      body.appendChild(a);
    }
    overlay.appendChild(heading);
    overlay.appendChild(body);

    // Hide all sibling panels + the tab strip so the overlay is the sole UI.
    for (const panel of root.querySelectorAll<HTMLElement>('.admin-panel')) {
      panel.hidden = true;
    }
    const strip = root.querySelector<HTMLElement>('[data-testid="admin-llm-tabs"]');
    if (strip) strip.hidden = true;
    for (const group of root.querySelectorAll<HTMLElement>('.admin-llm-tabpanel')) {
      group.hidden = true;
    }
    // Anchor the overlay near the top of the route shell — just after the
    // page header (the @dl/ui pageHeader landmark).
    const intro = root.querySelector('.dl-page-header');
    if (intro && intro.parentElement) {
      intro.parentElement.insertBefore(overlay, intro.nextSibling);
    } else {
      root.appendChild(overlay);
    }
    return true;
  }
  return false;
}

function setStatus(node: Element, text: string): void {
  node.textContent = text;
}

function clear(node: Element): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

// ──────────────────────────────────────────────────────────── providers ─

interface ProvidersCache {
  providers: Provider[];
  selectedProviderId: string | null;
}

const providersCache: ProvidersCache = { providers: [], selectedProviderId: null };

function renderProvidersList(listEl: Element, statusEl: Element): void {
  clear(listEl);
  if (providersCache.providers.length === 0) {
    setStatus(statusEl, t('admin.llm.providers.empty'));
    return;
  }
  setStatus(statusEl, '');
  const ul = document.createElement('ul');
  ul.setAttribute('class', 'admin-provider-list');
  ul.setAttribute('role', 'list');
  for (const p of providersCache.providers) {
    const liEl = document.createElement('li');
    liEl.setAttribute('class', 'admin-provider-row');
    liEl.setAttribute('data-provider-id', p.id);
    liEl.setAttribute('data-testid', `admin-provider-${p.id}`);

    const nameEl = document.createElement('span');
    nameEl.setAttribute('class', 'admin-provider-name');
    nameEl.textContent = p.display_name;
    liEl.appendChild(nameEl);

    const adapterEl = document.createElement('code');
    adapterEl.setAttribute('class', 'admin-provider-adapter');
    adapterEl.textContent = p.adapter_key;
    liEl.appendChild(adapterEl);

    if (!p.adapter_registered) {
      const warn = document.createElement('span');
      warn.setAttribute('class', 'admin-provider-warning');
      warn.setAttribute('role', 'note');
      warn.textContent = t('admin.llm.providers.adapter_missing');
      liEl.appendChild(warn);
    }

    const counts = document.createElement('span');
    counts.setAttribute('class', 'admin-provider-counts');
    counts.textContent = `${t('admin.llm.providers.models_count', { n: p.models_count })} · ${t('admin.llm.providers.keys_count', { n: p.active_keys_count })}`;
    liEl.appendChild(counts);

    // Enabled toggle — checkbox with associated label
    const toggleWrap = document.createElement('label');
    toggleWrap.setAttribute('class', 'admin-provider-toggle');
    toggleWrap.setAttribute('for', `admin-provider-enabled-${p.id}`);
    const toggle = document.createElement('input');
    toggle.setAttribute('type', 'checkbox');
    toggle.setAttribute('id', `admin-provider-enabled-${p.id}`);
    toggle.setAttribute('data-testid', `admin-provider-enabled-${p.id}`);
    if (p.enabled) toggle.setAttribute('checked', '');
    toggle.setAttribute(
      'aria-label',
      p.enabled
        ? t('admin.llm.providers.toggle_disable', { name: p.display_name })
        : t('admin.llm.providers.toggle_enable', { name: p.display_name }),
    );
    toggle.addEventListener('change', () => {
      void (async (): Promise<void> => {
        toggle.disabled = true;
        const res = await patchProvider(p.id, { enabled: toggle.checked });
        toggle.disabled = false;
        if (!res.ok) {
          // Revert and surface inline
          toggle.checked = p.enabled;
          setStatus(statusEl, t('admin.llm.error.generic'));
          return;
        }
        p.enabled = toggle.checked;
        toggle.setAttribute(
          'aria-label',
          p.enabled
            ? t('admin.llm.providers.toggle_disable', { name: p.display_name })
            : t('admin.llm.providers.toggle_enable', { name: p.display_name }),
        );
      })();
    });
    toggleWrap.appendChild(toggle);
    const toggleText = document.createElement('span');
    toggleText.textContent = p.enabled
      ? t('admin.llm.providers.enabled')
      : t('admin.llm.providers.disabled');
    toggleWrap.appendChild(toggleText);
    liEl.appendChild(toggleWrap);

    // Test connection button
    const testBtn = document.createElement('button');
    testBtn.setAttribute('type', 'button');
    testBtn.setAttribute('class', 'admin-provider-test');
    testBtn.setAttribute('data-testid', `admin-provider-test-${p.id}`);
    testBtn.setAttribute('aria-label', t('admin.llm.providers.test_label', { name: p.display_name }));
    testBtn.textContent = t('admin.llm.providers.test');
    const resultSpan = document.createElement('span');
    resultSpan.setAttribute('class', 'admin-provider-test-result');
    resultSpan.setAttribute('role', 'status');
    resultSpan.setAttribute('aria-live', 'polite');
    testBtn.addEventListener('click', () => {
      void (async (): Promise<void> => {
        resultSpan.textContent = t('admin.llm.providers.test_pending');
        testBtn.disabled = true;
        const res = await testProviderConnection(p.id);
        testBtn.disabled = false;
        if (!res.ok) {
          resultSpan.textContent = t('admin.llm.providers.test_error', { error: res.kind });
          return;
        }
        resultSpan.textContent = res.value.ok
          ? t('admin.llm.providers.test_ok')
          : t('admin.llm.providers.test_error', { error: res.value.error ?? '' });
      })();
    });
    liEl.appendChild(testBtn);
    liEl.appendChild(resultSpan);

    // Base URL — point a provider at a local / OpenAI-compatible endpoint
    // (Ollama, LM Studio, vLLM, …). Empty = the adapter's built-in default.
    const baseUrlWrap = document.createElement('label');
    baseUrlWrap.setAttribute('class', 'admin-provider-baseurl');
    baseUrlWrap.setAttribute('for', `admin-provider-baseurl-${p.id}`);
    const baseUrlLabel = document.createElement('span');
    baseUrlLabel.textContent = t('admin.llm.providers.base_url');
    const baseUrlInput = document.createElement('input');
    baseUrlInput.setAttribute('type', 'url');
    baseUrlInput.setAttribute('id', `admin-provider-baseurl-${p.id}`);
    baseUrlInput.setAttribute('data-testid', `admin-provider-baseurl-${p.id}`);
    baseUrlInput.setAttribute('placeholder', t('admin.llm.providers.base_url_placeholder'));
    baseUrlInput.value = p.base_url ?? '';
    const baseUrlSave = document.createElement('button');
    baseUrlSave.setAttribute('type', 'button');
    baseUrlSave.setAttribute('class', 'admin-provider-baseurl-save');
    baseUrlSave.setAttribute('data-testid', `admin-provider-baseurl-save-${p.id}`);
    baseUrlSave.textContent = t('admin.llm.providers.base_url_save');
    baseUrlSave.addEventListener('click', () => {
      void (async (): Promise<void> => {
        const next = baseUrlInput.value.trim() === '' ? null : baseUrlInput.value.trim();
        baseUrlSave.disabled = true;
        const res = await patchProvider(p.id, { base_url: next });
        baseUrlSave.disabled = false;
        if (!res.ok) {
          setStatus(statusEl, t('admin.llm.error.generic'));
          return;
        }
        p.base_url = next;
        setStatus(statusEl, t('admin.llm.providers.base_url_saved'));
      })();
    });
    baseUrlWrap.appendChild(baseUrlLabel);
    baseUrlWrap.appendChild(baseUrlInput);
    liEl.appendChild(baseUrlWrap);
    liEl.appendChild(baseUrlSave);

    ul.appendChild(liEl);
  }
  listEl.appendChild(ul);
}

function mountProviders(root: Element): () => void {
  const container = root.querySelector('[data-testid="admin-providers-panel"]');
  if (!container) return noopDisposer;
  const statusEl = container.querySelector('[data-providers-status]')!;
  const listEl = container.querySelector('[data-providers-list]')!;
  let cancelled = false;
  setStatus(statusEl, t('admin.llm.loading'));
  void (async (): Promise<void> => {
    const res = await listProviders();
    if (cancelled) return;
    if (maybeRenderTopLevelOverlay(container, res)) return;
    if (!res.ok) {
      setStatus(statusEl, t('admin.llm.error.generic'));
      return;
    }
    providersCache.providers = res.value.providers;
    if (
      providersCache.selectedProviderId === null &&
      providersCache.providers.length > 0
    ) {
      providersCache.selectedProviderId = providersCache.providers[0]!.id;
    }
    renderProvidersList(listEl, statusEl);
    // Broadcast a change so the dependent panels (models / keys) can
    // populate their provider selects.
    container.dispatchEvent(
      new CustomEvent('admin-providers-ready', {
        bubbles: true,
        detail: { providers: providersCache.providers },
      }),
    );
  })();
  return () => {
    cancelled = true;
  };
}

// ───────────────────────────────────────────────────────────── models ─

interface ModelsCache {
  providerId: string | null;
  models: Model[];
}

const modelsCache: ModelsCache = { providerId: null, models: [] };

function renderModelsList(listEl: Element, statusEl: Element): void {
  clear(listEl);
  if (modelsCache.providerId === null) {
    setStatus(statusEl, t('admin.llm.models.select_provider'));
    return;
  }
  setStatus(statusEl, modelsCache.models.length === 0 ? t('admin.llm.models.empty') : '');
  // The add-model form is always available once a provider is selected —
  // it's the dynamic-config path for local / OpenAI-compatible endpoints
  // whose model catalogue isn't shipped in code.
  if (modelsCache.models.length === 0) {
    listEl.appendChild(buildAddModelForm(modelsCache.providerId, listEl, statusEl));
    return;
  }
  const tableEl = document.createElement('table');
  tableEl.setAttribute('class', 'admin-models-table');
  tableEl.setAttribute('data-testid', 'admin-models-table');

  const theadEl = document.createElement('thead');
  const trEl = document.createElement('tr');
  for (const heading of [
    t('admin.llm.models.title'),
    t('admin.llm.models.cost_per_1m', { input: '$', output: '$' })
      .replace('$/', '$0/')
      .replace(/per 1M tokens/i, 'per 1M'),
  ]) {
    const thEl = document.createElement('th');
    thEl.setAttribute('scope', 'col');
    thEl.textContent = heading;
    trEl.appendChild(thEl);
  }
  theadEl.appendChild(trEl);
  tableEl.appendChild(theadEl);

  const tbodyEl = document.createElement('tbody');
  for (const m of modelsCache.models) {
    const rowEl = document.createElement('tr');
    rowEl.setAttribute('data-model-id', m.id);
    rowEl.setAttribute('data-testid', `admin-model-${m.id}`);

    const tdName = document.createElement('td');
    const nameSpan = document.createElement('span');
    nameSpan.textContent = m.display_name;
    tdName.appendChild(nameSpan);
    if (m.deprecated) {
      const dep = document.createElement('span');
      dep.setAttribute('class', 'admin-model-deprecated');
      dep.textContent = ` (${t('admin.llm.models.deprecated')})`;
      tdName.appendChild(dep);
    }
    const keySpan = document.createElement('code');
    keySpan.setAttribute('class', 'admin-model-key');
    keySpan.textContent = m.model_key;
    tdName.appendChild(document.createElement('br'));
    tdName.appendChild(keySpan);
    rowEl.appendChild(tdName);

    const tdCost = document.createElement('td');
    tdCost.textContent = t('admin.llm.models.cost_per_1m', {
      input: m.cost_input_per_1m.toFixed(2),
      output: m.cost_output_per_1m.toFixed(2),
    });
    rowEl.appendChild(tdCost);

    tbodyEl.appendChild(rowEl);
  }
  tableEl.appendChild(tbodyEl);
  listEl.appendChild(tableEl);
  listEl.appendChild(buildAddModelForm(modelsCache.providerId, listEl, statusEl));
}

/**
 * "Register a model" form — POSTs to the provider's models endpoint.
 * Lets an admin add models for a local / OpenAI-compatible provider
 * (e.g. `llama3.1:8b`) without a code change. Reloads the list on success.
 */
function buildAddModelForm(providerId: string, listEl: Element, statusEl: Element): HTMLElement {
  const form = document.createElement('form');
  form.setAttribute('class', 'admin-add-model');
  form.setAttribute('data-testid', 'admin-add-model-form');

  const keyInput = document.createElement('input');
  keyInput.setAttribute('type', 'text');
  keyInput.setAttribute('data-testid', 'admin-add-model-key');
  keyInput.setAttribute('aria-label', t('admin.llm.models.add_key_label'));
  keyInput.setAttribute('placeholder', t('admin.llm.models.add_key_placeholder'));

  const nameInput = document.createElement('input');
  nameInput.setAttribute('type', 'text');
  nameInput.setAttribute('data-testid', 'admin-add-model-name');
  nameInput.setAttribute('aria-label', t('admin.llm.models.add_name_label'));
  nameInput.setAttribute('placeholder', t('admin.llm.models.add_name_placeholder'));

  const addBtn = document.createElement('button');
  addBtn.setAttribute('type', 'submit');
  addBtn.setAttribute('data-testid', 'admin-add-model-submit');
  addBtn.textContent = t('admin.llm.models.add_submit');

  form.appendChild(keyInput);
  form.appendChild(nameInput);
  form.appendChild(addBtn);
  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    const modelKey = keyInput.value.trim();
    if (modelKey === '') return;
    void (async (): Promise<void> => {
      addBtn.disabled = true;
      const res = await createModel(providerId, {
        model_key: modelKey,
        ...(nameInput.value.trim() !== '' ? { display_name: nameInput.value.trim() } : {}),
      });
      addBtn.disabled = false;
      if (!res.ok) {
        setStatus(statusEl, t('admin.llm.error.generic'));
        return;
      }
      keyInput.value = '';
      nameInput.value = '';
      await loadModels(providerId, listEl, statusEl);
    })();
  });
  return form;
}

async function loadModels(providerId: string, listEl: Element, statusEl: Element): Promise<void> {
  modelsCache.providerId = providerId;
  modelsCache.models = [];
  setStatus(statusEl, t('admin.llm.loading'));
  const res = await listModels(providerId);
  if (maybeRenderTopLevelOverlay(listEl, res)) return;
  if (!res.ok) {
    setStatus(statusEl, t('admin.llm.error.generic'));
    return;
  }
  modelsCache.models = res.value.models;
  renderModelsList(listEl, statusEl);
}

function buildProviderSelect(
  id: string,
  testid: string,
  onChange: (providerId: string) => void,
): HTMLSelectElement {
  const sel = document.createElement('select');
  sel.setAttribute('id', id);
  sel.setAttribute('data-testid', testid);
  sel.setAttribute('aria-label', t('admin.llm.providers.select_label'));
  for (const p of providersCache.providers) {
    const opt = document.createElement('option');
    opt.setAttribute('value', p.id);
    opt.textContent = p.display_name;
    sel.appendChild(opt);
  }
  if (providersCache.selectedProviderId !== null) {
    sel.value = providersCache.selectedProviderId;
  }
  sel.addEventListener('change', () => {
    onChange(sel.value);
  });
  return sel;
}

function mountModels(root: Element): () => void {
  const container = root.querySelector('[data-testid="admin-models-panel"]');
  if (!container) return noopDisposer;
  const statusEl = container.querySelector('[data-models-status]')!;
  const selectMount = container.querySelector('[data-models-provider-select]')!;
  const listEl = container.querySelector('[data-models-list]')!;
  setStatus(statusEl, t('admin.llm.models.select_provider'));

  const onProvidersReady = (): void => {
    clear(selectMount);
    const labelEl = document.createElement('label');
    labelEl.setAttribute('for', 'admin-models-provider-select');
    labelEl.textContent = t('admin.llm.providers.select_label');
    selectMount.appendChild(labelEl);
    const sel = buildProviderSelect(
      'admin-models-provider-select',
      'admin-models-provider-select',
      (pid) => {
        void loadModels(pid, listEl, statusEl);
      },
    );
    selectMount.appendChild(sel);
    if (providersCache.selectedProviderId !== null) {
      void loadModels(providersCache.selectedProviderId, listEl, statusEl);
    }
  };

  document.addEventListener('admin-providers-ready', onProvidersReady);
  if (providersCache.providers.length > 0) onProvidersReady();
  return () => {
    document.removeEventListener('admin-providers-ready', onProvidersReady);
  };
}

// ───────────────────────────────────────────────────────────────── keys ─

interface KeysCache {
  providerId: string | null;
  keys: import('./api.js').KeySummary[];
}

const keysCache: KeysCache = { providerId: null, keys: [] };

function renderKeysList(
  listEl: Element,
  statusEl: Element,
  reload: () => void,
): void {
  clear(listEl);
  if (keysCache.providerId === null) {
    setStatus(statusEl, t('admin.llm.keys.select_provider'));
    return;
  }
  if (keysCache.keys.length === 0) {
    setStatus(statusEl, t('admin.llm.keys.empty'));
    return;
  }
  setStatus(statusEl, '');
  const ul = document.createElement('ul');
  ul.setAttribute('class', 'admin-keys-list');
  ul.setAttribute('role', 'list');
  for (const k of keysCache.keys) {
    const liEl = document.createElement('li');
    liEl.setAttribute('class', 'admin-key-row');
    liEl.setAttribute('data-key-id', k.id);
    liEl.setAttribute('data-testid', `admin-key-${k.id}`);

    const labelSpan = document.createElement('span');
    labelSpan.setAttribute('class', 'admin-key-label');
    labelSpan.textContent = k.label;
    liEl.appendChild(labelSpan);

    const lastFourSpan = document.createElement('code');
    lastFourSpan.setAttribute('class', 'admin-key-last-four');
    lastFourSpan.setAttribute('data-testid', `admin-key-last-four-${k.id}`);
    lastFourSpan.textContent = t('admin.llm.keys.last_four', { four: k.last_four });
    liEl.appendChild(lastFourSpan);

    const badge = document.createElement('span');
    badge.setAttribute('class', 'admin-key-badge');
    badge.setAttribute(
      'data-state',
      k.retired_at !== null ? 'retired' : 'active',
    );
    badge.textContent =
      k.retired_at !== null
        ? t('admin.llm.keys.retired_badge')
        : t('admin.llm.keys.active_badge');
    liEl.appendChild(badge);

    const createdSpan = document.createElement('span');
    createdSpan.setAttribute('class', 'admin-key-created');
    createdSpan.textContent = t('admin.llm.keys.created_at', { at: formatAt(k.created_at) });
    liEl.appendChild(createdSpan);

    if (k.retired_at === null) {
      const rotateBtn = document.createElement('button');
      rotateBtn.setAttribute('type', 'button');
      rotateBtn.setAttribute('class', 'admin-key-rotate');
      rotateBtn.setAttribute('aria-label', t('admin.llm.keys.rotate_label', { label: k.label }));
      rotateBtn.setAttribute('data-testid', `admin-key-rotate-${k.id}`);
      rotateBtn.textContent = t('admin.llm.keys.rotate');
      rotateBtn.addEventListener('click', () => {
        void (async (): Promise<void> => {
          const plaintext = window.prompt(t('admin.llm.keys.plaintext_field'));
          if (!plaintext || plaintext.length < 8) return;
          rotateBtn.disabled = true;
          const res = await rotateKey(k.id, { plaintext_key: plaintext });
          rotateBtn.disabled = false;
          if (!res.ok) {
            setStatus(statusEl, t('admin.llm.error.generic'));
            return;
          }
          reload();
        })();
      });
      liEl.appendChild(rotateBtn);

      const retireBtn = document.createElement('button');
      retireBtn.setAttribute('type', 'button');
      retireBtn.setAttribute('class', 'admin-key-retire');
      retireBtn.setAttribute('aria-label', t('admin.llm.keys.retire_label', { label: k.label }));
      retireBtn.setAttribute('data-testid', `admin-key-retire-${k.id}`);
      retireBtn.textContent = t('admin.llm.keys.retire');
      retireBtn.addEventListener('click', () => {
        void (async (): Promise<void> => {
          retireBtn.disabled = true;
          const res = await retireKey(k.id);
          retireBtn.disabled = false;
          if (!res.ok) {
            setStatus(statusEl, t('admin.llm.error.generic'));
            return;
          }
          reload();
        })();
      });
      liEl.appendChild(retireBtn);
    } else {
      const retiredAt = document.createElement('span');
      retiredAt.setAttribute('class', 'admin-key-retired-at');
      retiredAt.textContent = t('admin.llm.keys.retired_at', {
        at: formatAt(k.retired_at),
      });
      liEl.appendChild(retiredAt);
    }

    ul.appendChild(liEl);
  }
  listEl.appendChild(ul);
}

async function loadKeys(
  providerId: string,
  listEl: Element,
  statusEl: Element,
  reload: () => void,
): Promise<void> {
  keysCache.providerId = providerId;
  keysCache.keys = [];
  setStatus(statusEl, t('admin.llm.loading'));
  const res = await listKeys(providerId);
  if (maybeRenderTopLevelOverlay(listEl, res)) return;
  if (!res.ok) {
    setStatus(statusEl, t('admin.llm.error.generic'));
    return;
  }
  keysCache.keys = res.value.keys;
  renderKeysList(listEl, statusEl, reload);
}

function buildAddKeyForm(
  formEl: HTMLFormElement,
  feedbackEl: Element,
  reload: () => void,
): void {
  formEl.addEventListener('submit', (event) => {
    event.preventDefault();
    const data = new FormData(formEl);
    const labelInput = formEl.querySelector<HTMLInputElement>('#admin-key-label');
    const plaintextInput = formEl.querySelector<HTMLInputElement>('#admin-key-plaintext');
    const labelErr = formEl.querySelector<HTMLElement>('[data-error-for="admin-key-label"]');
    const plaintextErr = formEl.querySelector<HTMLElement>(
      '[data-error-for="admin-key-plaintext"]',
    );
    if (labelErr) labelErr.textContent = '';
    if (plaintextErr) plaintextErr.textContent = '';
    if (labelInput) labelInput.removeAttribute('aria-invalid');
    if (plaintextInput) plaintextInput.removeAttribute('aria-invalid');
    feedbackEl.textContent = '';

    const label = String(data.get('label') ?? '').trim();
    const plaintext = String(data.get('plaintext_key') ?? '');

    let hasError = false;
    if (label.length === 0) {
      if (labelErr) labelErr.textContent = t('admin.llm.keys.error_label_required');
      if (labelInput) labelInput.setAttribute('aria-invalid', 'true');
      hasError = true;
    }
    if (plaintext.length < 8) {
      if (plaintextErr) plaintextErr.textContent = t('admin.llm.keys.error_plaintext_required');
      if (plaintextInput) plaintextInput.setAttribute('aria-invalid', 'true');
      hasError = true;
    }
    if (hasError) {
      const first = formEl.querySelector<HTMLElement>('[aria-invalid="true"]');
      first?.focus();
      return;
    }

    const providerId = keysCache.providerId;
    if (providerId === null) return;
    void (async (): Promise<void> => {
      const submit = formEl.querySelector<HTMLButtonElement>('button[type="submit"]');
      if (submit) submit.disabled = true;
      const res = await addKey(providerId, { plaintext_key: plaintext, label });
      if (submit) submit.disabled = false;
      if (!res.ok) {
        feedbackEl.textContent = t('admin.llm.error.generic');
        return;
      }
      // Wipe the plaintext from the DOM immediately. The server response
      // never contains the plaintext — only the last-4 — and we display
      // only the last-4.
      if (plaintextInput) plaintextInput.value = '';
      if (labelInput) labelInput.value = '';
      feedbackEl.textContent = t('admin.llm.keys.added_confirmation', {
        four: res.value.last_four,
      });
      reload();
    })();
  });
}

function mountKeys(root: Element): () => void {
  const container = root.querySelector('[data-testid="admin-keys-panel"]');
  if (!container) return noopDisposer;
  const statusEl = container.querySelector('[data-keys-status]')!;
  const selectMount = container.querySelector('[data-keys-provider-select]')!;
  const listEl = container.querySelector('[data-keys-list]')!;
  const formEl = container.querySelector<HTMLFormElement>('[data-keys-form]')!;
  const feedbackEl = container.querySelector('[data-keys-feedback]')!;
  setStatus(statusEl, t('admin.llm.keys.select_provider'));

  const reload = (): void => {
    if (keysCache.providerId !== null) {
      void loadKeys(keysCache.providerId, listEl, statusEl, reload);
    }
  };

  const onProvidersReady = (): void => {
    clear(selectMount);
    const labelEl = document.createElement('label');
    labelEl.setAttribute('for', 'admin-keys-provider-select');
    labelEl.textContent = t('admin.llm.providers.select_label');
    selectMount.appendChild(labelEl);
    const sel = buildProviderSelect(
      'admin-keys-provider-select',
      'admin-keys-provider-select',
      (pid) => {
        void loadKeys(pid, listEl, statusEl, reload);
      },
    );
    selectMount.appendChild(sel);
    if (providersCache.selectedProviderId !== null) {
      void loadKeys(providersCache.selectedProviderId, listEl, statusEl, reload);
    }
  };

  buildAddKeyForm(formEl, feedbackEl, reload);

  document.addEventListener('admin-providers-ready', onProvidersReady);
  if (providersCache.providers.length > 0) onProvidersReady();
  return () => {
    document.removeEventListener('admin-providers-ready', onProvidersReady);
  };
}

// ─────────────────────────────────────────────────────────── bindings ─

interface BindingsCache {
  bindings: Binding[];
}

const bindingsCache: BindingsCache = { bindings: [] };

function bindingFor(functionKey: string, posture: string): Binding | null {
  return (
    bindingsCache.bindings.find(
      (b) => b.function_key === functionKey && b.posture === posture,
    ) ?? null
  );
}

async function buildModelChoices(providerId: string): Promise<Model[]> {
  const res = await listModels(providerId);
  if (!res.ok) return [];
  return res.value.models;
}

function renderBindingsMatrix(matrixEl: Element, statusEl: Element): void {
  clear(matrixEl);
  if (providersCache.providers.length === 0) {
    setStatus(statusEl, t('admin.llm.bindings.empty'));
    return;
  }
  setStatus(statusEl, '');
  const tableEl = document.createElement('table');
  tableEl.setAttribute('class', 'admin-bindings-matrix');
  tableEl.setAttribute('data-testid', 'admin-bindings-matrix');

  const theadEl = document.createElement('thead');
  const headRow = document.createElement('tr');
  const blankTh = document.createElement('th');
  blankTh.setAttribute('scope', 'col');
  blankTh.textContent = t('admin.llm.bindings.function');
  headRow.appendChild(blankTh);
  for (const posture of POSTURES) {
    const thEl = document.createElement('th');
    thEl.setAttribute('scope', 'col');
    thEl.textContent = posture;
    headRow.appendChild(thEl);
  }
  theadEl.appendChild(headRow);
  tableEl.appendChild(theadEl);

  const tbodyEl = document.createElement('tbody');
  for (const fn of TASK_KINDS) {
    const rowEl = document.createElement('tr');
    const rowHead = document.createElement('th');
    rowHead.setAttribute('scope', 'row');
    rowHead.textContent = fn;
    rowEl.appendChild(rowHead);
    for (const posture of POSTURES) {
      const tdEl = document.createElement('td');
      const current = bindingFor(fn, posture);
      const selectId = `admin-binding-${fn}-${posture}`;
      const sel = document.createElement('select');
      sel.setAttribute('id', selectId);
      sel.setAttribute('data-testid', selectId);
      sel.setAttribute(
        'aria-label',
        t('admin.llm.bindings.cell_label', { function: fn, posture }),
      );

      // Empty option
      const emptyOpt = document.createElement('option');
      emptyOpt.setAttribute('value', '');
      emptyOpt.textContent = '—';
      sel.appendChild(emptyOpt);

      // For each provider, listModels lazily — we already fetched the
      // models on demand via the dedicated panel. Here we hydrate the
      // cell selects from each provider's model list. We keep the
      // existing binding selected via dataset until the matching option
      // arrives.
      const desiredValue =
        current !== null ? `${current.provider_id}::${current.model_id}` : '';
      sel.value = desiredValue;
      sel.dataset['desiredValue'] = desiredValue;

      const feedback = document.createElement('span');
      feedback.setAttribute('class', 'admin-binding-feedback');
      feedback.setAttribute('role', 'status');
      feedback.setAttribute('aria-live', 'polite');
      feedback.setAttribute('data-testid', `${selectId}-feedback`);

      sel.addEventListener('change', () => {
        const raw = sel.value;
        if (raw === '') return;
        const [providerId, modelId] = raw.split('::');
        if (!providerId || !modelId) return;
        feedback.textContent = '';
        void (async (): Promise<void> => {
          sel.disabled = true;
          const res = await setBinding({
            function_key: fn,
            posture,
            provider_id: providerId,
            model_id: modelId,
          });
          sel.disabled = false;
          if (!res.ok) {
            feedback.textContent = t('admin.llm.bindings.save_error', {
              error: res.kind === 'error' ? res.error : res.kind,
            });
            return;
          }
          feedback.textContent = t('admin.llm.bindings.saved');
          // Refresh local cache for subsequent renders.
          const existing = bindingFor(fn, posture);
          if (existing) {
            existing.provider_id = providerId;
            existing.model_id = modelId;
          } else {
            bindingsCache.bindings.push({
              id: `tmp:${fn}:${posture}`,
              function_key: fn,
              posture,
              provider_id: providerId,
              model_id: modelId,
              config: null,
              provider_adapter_key: '',
              provider_display_name: '',
              model_key: '',
              model_display_name: '',
              created_at: '',
              updated_at: '',
            });
          }
        })();
      });

      tdEl.appendChild(sel);
      tdEl.appendChild(feedback);
      rowEl.appendChild(tdEl);
    }
    tbodyEl.appendChild(rowEl);
  }
  tableEl.appendChild(tbodyEl);
  matrixEl.appendChild(tableEl);

  // Asynchronously hydrate each cell's <select> with the provider's
  // model options. We collect models per provider once.
  void (async (): Promise<void> => {
    const modelsByProvider = new Map<string, Model[]>();
    for (const p of providersCache.providers) {
      modelsByProvider.set(p.id, await buildModelChoices(p.id));
    }
    // Scope to the binding cells (tableEl) — the generation-settings panel
    // appended below also contains <select>s that must NOT be hydrated.
    for (const sel of tableEl.querySelectorAll<HTMLSelectElement>('select')) {
      const desired = sel.dataset['desiredValue'] ?? '';
      // Clear all but the empty option, then rebuild.
      while (sel.options.length > 1) sel.remove(1);
      for (const p of providersCache.providers) {
        const optgroup = document.createElement('optgroup');
        optgroup.setAttribute('label', p.display_name);
        for (const m of modelsByProvider.get(p.id) ?? []) {
          const opt = document.createElement('option');
          opt.setAttribute('value', `${p.id}::${m.id}`);
          opt.textContent = m.display_name;
          if (m.deprecated) opt.setAttribute('disabled', '');
          optgroup.appendChild(opt);
        }
        sel.appendChild(optgroup);
      }
      sel.value = desired;
    }
  })();

  // Per-binding generation knobs (reasoning effort for text; image size /
  // steps for images), keyed off each function's standard-posture binding.
  renderGenerationSettings(matrixEl);
}

/** A `config` patch — an explicit `undefined` value clears that key. */
type BindingConfigPatch = {
  [K in keyof BindingConfig]?: BindingConfig[K] | undefined;
};

/**
 * Persist a partial `config` patch onto a function's standard-posture
 * binding. Keys set to `undefined` are dropped (revert to the model /
 * provider default). No binding → surfaces the "bind first" hint.
 */
async function saveBindingConfig(
  functionKey: string,
  patch: BindingConfigPatch,
  feedbackEl: Element,
): Promise<void> {
  const binding = bindingFor(functionKey, 'standard');
  if (!binding) {
    feedbackEl.textContent = t('admin.llm.gen.no_binding');
    return;
  }
  const merged: Record<string, unknown> = { ...parseBindingConfig(binding.config) };
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) delete merged[k];
    else merged[k] = v;
  }
  const next = merged as BindingConfig;
  feedbackEl.textContent = '';
  const res = await setBinding({
    function_key: functionKey,
    posture: 'standard',
    provider_id: binding.provider_id,
    model_id: binding.model_id,
    config: next,
  });
  if (!res.ok) {
    feedbackEl.textContent = t('admin.llm.gen.save_error', {
      error: res.kind === 'error' ? res.error : res.kind,
    });
    return;
  }
  binding.config = Object.keys(next).length === 0 ? null : JSON.stringify(next);
  feedbackEl.textContent = t('admin.llm.gen.saved');
}

/** A label + control + help row inside the generation-settings panel. */
function genField(labelText: string, helpText: string | null, control: Element): Element {
  const field = document.createElement('div');
  field.setAttribute('class', 'admin-gen-field');
  const label = document.createElement('label');
  label.setAttribute('class', 'admin-gen-label');
  label.textContent = labelText;
  if (control.id) label.setAttribute('for', control.id);
  field.appendChild(label);
  field.appendChild(control);
  if (helpText) {
    const help = document.createElement('p');
    help.setAttribute('class', 'admin-gen-help');
    help.textContent = helpText;
    field.appendChild(help);
  }
  return field;
}

function renderGenerationSettings(parentEl: Element): void {
  const section = document.createElement('section');
  section.setAttribute('class', 'admin-gen-settings');
  section.setAttribute('data-testid', 'admin-gen-settings');

  const heading = document.createElement('h3');
  heading.setAttribute('class', 'admin-gen-heading');
  heading.textContent = t('admin.llm.gen.title');
  section.appendChild(heading);

  const body = document.createElement('p');
  body.setAttribute('class', 'admin-gen-body');
  body.textContent = t('admin.llm.gen.body');
  section.appendChild(body);

  // ── Text: reasoning effort (prose_suggest) ──
  {
    const proseBinding = bindingFor('prose_suggest', 'standard');
    const cfg = parseBindingConfig(proseBinding?.config);
    const feedback = document.createElement('span');
    feedback.setAttribute('class', 'admin-gen-feedback');
    feedback.setAttribute('role', 'status');
    feedback.setAttribute('aria-live', 'polite');
    feedback.setAttribute('data-testid', 'admin-gen-reasoning-feedback');

    const sel = document.createElement('select');
    sel.setAttribute('id', 'admin-gen-reasoning');
    sel.setAttribute('data-testid', 'admin-gen-reasoning');
    const reasoningOpts: [string, string][] = [
      ['', t('admin.llm.gen.reasoning_default')],
      ['none', t('admin.llm.gen.reasoning_none')],
      ['low', t('admin.llm.gen.reasoning_low')],
      ['medium', t('admin.llm.gen.reasoning_medium')],
      ['high', t('admin.llm.gen.reasoning_high')],
    ];
    for (const [value, label] of reasoningOpts) {
      const opt = document.createElement('option');
      opt.setAttribute('value', value);
      opt.textContent = label;
      sel.appendChild(opt);
    }
    sel.value = cfg.reasoning_effort ?? '';
    if (!proseBinding) sel.disabled = true;
    sel.addEventListener('change', () => {
      const v = sel.value;
      void saveBindingConfig(
        'prose_suggest',
        { reasoning_effort: v === '' ? undefined : (v as BindingConfig['reasoning_effort']) },
        feedback,
      );
    });

    section.appendChild(
      genField(t('admin.llm.gen.reasoning_label'), t('admin.llm.gen.reasoning_help'), sel),
    );
    section.appendChild(feedback);
    if (!proseBinding) {
      const hint = document.createElement('p');
      hint.setAttribute('class', 'admin-gen-help');
      hint.textContent = t('admin.llm.gen.no_binding');
      section.appendChild(hint);
    }
  }

  // ── Image: size + steps (image_generate) ──
  {
    const imgBinding = bindingFor('image_generate', 'standard');
    const cfg = parseBindingConfig(imgBinding?.config);

    // Size
    const sizeFeedback = document.createElement('span');
    sizeFeedback.setAttribute('class', 'admin-gen-feedback');
    sizeFeedback.setAttribute('role', 'status');
    sizeFeedback.setAttribute('aria-live', 'polite');
    sizeFeedback.setAttribute('data-testid', 'admin-gen-size-feedback');

    const sizeSel = document.createElement('select');
    sizeSel.setAttribute('id', 'admin-gen-size');
    sizeSel.setAttribute('data-testid', 'admin-gen-size');
    const sizeOpts: [string, string][] = [
      ['', t('admin.llm.gen.size_default')],
      ['256x256', '256×256'],
      ['512x512', '512×512'],
      ['768x768', '768×768'],
      ['1024x1024', '1024×1024'],
    ];
    for (const [value, label] of sizeOpts) {
      const opt = document.createElement('option');
      opt.setAttribute('value', value);
      opt.textContent = label;
      sizeSel.appendChild(opt);
    }
    sizeSel.value = cfg.size ?? '';
    if (!imgBinding) sizeSel.disabled = true;
    sizeSel.addEventListener('change', () => {
      const v = sizeSel.value;
      void saveBindingConfig('image_generate', { size: v === '' ? undefined : v }, sizeFeedback);
    });
    section.appendChild(genField(t('admin.llm.gen.size_label'), null, sizeSel));
    section.appendChild(sizeFeedback);

    // Steps
    const stepsFeedback = document.createElement('span');
    stepsFeedback.setAttribute('class', 'admin-gen-feedback');
    stepsFeedback.setAttribute('role', 'status');
    stepsFeedback.setAttribute('aria-live', 'polite');
    stepsFeedback.setAttribute('data-testid', 'admin-gen-steps-feedback');

    const stepsInput = document.createElement('input');
    stepsInput.setAttribute('id', 'admin-gen-steps');
    stepsInput.setAttribute('data-testid', 'admin-gen-steps');
    stepsInput.setAttribute('type', 'number');
    stepsInput.setAttribute('min', '1');
    stepsInput.setAttribute('max', '150');
    stepsInput.setAttribute('placeholder', t('admin.llm.gen.steps_placeholder'));
    if (cfg.steps !== undefined) stepsInput.value = String(cfg.steps);
    if (!imgBinding) stepsInput.disabled = true;
    stepsInput.addEventListener('change', () => {
      const raw = stepsInput.value.trim();
      const n = raw === '' ? undefined : Number.parseInt(raw, 10);
      void saveBindingConfig(
        'image_generate',
        { steps: n !== undefined && Number.isInteger(n) && n > 0 ? n : undefined },
        stepsFeedback,
      );
    });
    section.appendChild(
      genField(t('admin.llm.gen.steps_label'), t('admin.llm.gen.steps_help'), stepsInput),
    );
    section.appendChild(stepsFeedback);

    if (!imgBinding) {
      const hint = document.createElement('p');
      hint.setAttribute('class', 'admin-gen-help');
      hint.textContent = t('admin.llm.gen.no_binding');
      section.appendChild(hint);
    }
  }

  parentEl.appendChild(section);
}

function mountBindings(root: Element): () => void {
  const container = root.querySelector('[data-testid="admin-bindings-panel"]');
  if (!container) return noopDisposer;
  const statusEl = container.querySelector('[data-bindings-status]')!;
  const matrixEl = container.querySelector('[data-bindings-matrix]')!;
  setStatus(statusEl, t('admin.llm.loading'));
  let cancelled = false;

  const renderIfReady = (): void => {
    if (providersCache.providers.length === 0) return;
    renderBindingsMatrix(matrixEl, statusEl);
  };

  void (async (): Promise<void> => {
    const res = await listBindings();
    if (cancelled) return;
    if (maybeRenderTopLevelOverlay(container, res)) return;
    if (!res.ok) {
      setStatus(statusEl, t('admin.llm.error.generic'));
      return;
    }
    bindingsCache.bindings = res.value.bindings;
    renderIfReady();
  })();
  const onProvidersReady = (): void => renderIfReady();
  document.addEventListener('admin-providers-ready', onProvidersReady);
  return () => {
    cancelled = true;
    document.removeEventListener('admin-providers-ready', onProvidersReady);
  };
}

// ───────────────────────────────────────────────── prompt templates ─

function renderTemplateEditor(tpl: PromptTemplate, reload: () => void): Element {
  const wrap = document.createElement('div');
  wrap.setAttribute('class', 'admin-template');
  wrap.setAttribute('data-testid', `admin-template-${tpl.key}`);

  const head = document.createElement('div');
  head.setAttribute('class', 'admin-template-head');
  const title = document.createElement('h3');
  title.setAttribute('class', 'admin-template-key');
  // Stable id so the textarea below can borrow the heading as its
  // accessible name (WCAG 2.1 AA — the editable field must have a label).
  const titleId = `admin-template-title-${tpl.key}`;
  title.setAttribute('id', titleId);
  title.textContent = tpl.key;
  head.appendChild(title);
  if (tpl.is_override) {
    const badge = document.createElement('span');
    badge.setAttribute('class', 'admin-template-badge');
    badge.textContent = t('admin.llm.templates.edited_badge');
    head.appendChild(badge);
  }
  wrap.appendChild(head);

  const legend = document.createElement('p');
  legend.setAttribute('class', 'admin-template-legend');
  legend.textContent = t('admin.llm.templates.placeholders', {
    tokens: tpl.placeholders.join(' '),
  });
  wrap.appendChild(legend);

  const ta = document.createElement('textarea');
  ta.setAttribute('class', 'admin-template-body');
  ta.setAttribute('data-testid', `admin-template-${tpl.key}-body`);
  ta.setAttribute('aria-labelledby', titleId);
  ta.setAttribute('rows', '6');
  ta.value = tpl.body;
  wrap.appendChild(ta);

  // Non-blocking guard: warn (live) when a required placeholder is missing
  // — deleting e.g. {predicate} / {fields} would quietly break grounding.
  const warning = document.createElement('p');
  warning.setAttribute('class', 'admin-template-warning');
  warning.setAttribute('role', 'alert');
  warning.setAttribute('data-testid', `admin-template-${tpl.key}-warning`);
  warning.hidden = true;
  const refreshWarning = (): void => {
    const missing = tpl.required.filter((token) => !ta.value.includes(token));
    if (missing.length === 0) {
      warning.hidden = true;
      warning.textContent = '';
    } else {
      warning.hidden = false;
      warning.textContent = t('admin.llm.templates.missing_required', {
        tokens: missing.join(' '),
      });
    }
  };
  ta.addEventListener('input', refreshWarning);
  refreshWarning();
  wrap.appendChild(warning);

  const feedback = document.createElement('span');
  feedback.setAttribute('class', 'admin-template-feedback');
  feedback.setAttribute('role', 'status');
  feedback.setAttribute('aria-live', 'polite');

  const actions = document.createElement('div');
  actions.setAttribute('class', 'admin-template-actions');

  const saveBtn = document.createElement('button');
  saveBtn.setAttribute('type', 'button');
  saveBtn.setAttribute('class', 'admin-template-save');
  saveBtn.setAttribute('data-testid', `admin-template-${tpl.key}-save`);
  saveBtn.textContent = t('admin.llm.templates.save');
  saveBtn.addEventListener('click', () => {
    feedback.textContent = '';
    void (async (): Promise<void> => {
      saveBtn.disabled = true;
      const res = await setTemplate({ template_key: tpl.key, body: ta.value });
      saveBtn.disabled = false;
      if (!res.ok) {
        feedback.textContent = t('admin.llm.templates.save_error', {
          error: res.kind === 'error' ? res.error : res.kind,
        });
        return;
      }
      reload();
    })();
  });
  actions.appendChild(saveBtn);

  const resetBtn = document.createElement('button');
  resetBtn.setAttribute('type', 'button');
  resetBtn.setAttribute('class', 'admin-template-reset');
  resetBtn.setAttribute('data-testid', `admin-template-${tpl.key}-reset`);
  resetBtn.textContent = t('admin.llm.templates.reset');
  if (!tpl.is_override) resetBtn.disabled = true;
  resetBtn.addEventListener('click', () => {
    feedback.textContent = '';
    void (async (): Promise<void> => {
      resetBtn.disabled = true;
      const res = await setTemplate({ template_key: tpl.key, reset: true });
      if (!res.ok) {
        resetBtn.disabled = false;
        feedback.textContent = t('admin.llm.templates.save_error', {
          error: res.kind === 'error' ? res.error : res.kind,
        });
        return;
      }
      reload();
    })();
  });
  actions.appendChild(resetBtn);
  actions.appendChild(feedback);
  wrap.appendChild(actions);

  return wrap;
}

function mountTemplates(root: Element): () => void {
  const container = root.querySelector('[data-testid="admin-templates-panel"]');
  if (!container) return noopDisposer;
  const statusEl = container.querySelector('[data-templates-status]')!;
  const listEl = container.querySelector('[data-templates-list]')!;
  let cancelled = false;

  const load = async (): Promise<void> => {
    setStatus(statusEl, t('admin.llm.loading'));
    const res = await listTemplates();
    if (cancelled) return;
    if (maybeRenderTopLevelOverlay(container, res)) return;
    if (!res.ok) {
      setStatus(statusEl, t('admin.llm.error.generic'));
      return;
    }
    setStatus(statusEl, '');
    clear(listEl);
    for (const tpl of res.value.templates) {
      listEl.appendChild(renderTemplateEditor(tpl, () => void load()));
    }
  };
  void load();
  return () => {
    cancelled = true;
  };
}

// ──────────────────────────────── prompt-template version history (#165) ─

/** Locale-aware relative time (e.g. "2 hours ago"). Falls back to ISO. */
function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diffMs = then - Date.now();
  const locale =
    typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en';
  let rtf: Intl.RelativeTimeFormat;
  try {
    rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  } catch {
    rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  }
  const abs = Math.abs(diffMs);
  const sec = 1000;
  const min = 60 * sec;
  const hour = 60 * min;
  const day = 24 * hour;
  if (abs < min) return rtf.format(Math.round(diffMs / sec), 'second');
  if (abs < hour) return rtf.format(Math.round(diffMs / min), 'minute');
  if (abs < day) return rtf.format(Math.round(diffMs / hour), 'hour');
  if (abs < 30 * day) return rtf.format(Math.round(diffMs / day), 'day');
  if (abs < 365 * day) return rtf.format(Math.round(diffMs / (30 * day)), 'month');
  return rtf.format(Math.round(diffMs / (365 * day)), 'year');
}

/** One diff line: marker + text. `eq` = unchanged, `add`/`del` = changed. */
interface DiffLine {
  op: 'eq' | 'add' | 'del';
  text: string;
}

/**
 * A simple, dependency-free line-level diff (LCS over lines). `del` lines come
 * from `a` (the version being viewed), `add` lines from `b` (the current
 * version). Good enough for short prompt templates; no word-level granularity.
 * Renders with +/− markers so the diff never relies on colour alone.
 */
function lineDiff(a: string, b: string): DiffLine[] {
  const aLines = a.split('\n');
  const bLines = b.split('\n');
  const n = aLines.length;
  const m = bLines.length;
  // LCS length table.
  const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i]![j] =
        aLines[i] === bLines[j]
          ? lcs[i + 1]![j + 1]! + 1
          : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
    }
  }
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (aLines[i] === bLines[j]) {
      out.push({ op: 'eq', text: aLines[i]! });
      i++;
      j++;
    } else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) {
      out.push({ op: 'del', text: aLines[i]! });
      i++;
    } else {
      out.push({ op: 'add', text: bLines[j]! });
      j++;
    }
  }
  while (i < n) out.push({ op: 'del', text: aLines[i++]! });
  while (j < m) out.push({ op: 'add', text: bLines[j++]! });
  return out;
}

/** Render the +/− diff of a viewed version against the current body. */
function renderDiff(viewedBody: string, currentBody: string): Element {
  const wrap = document.createElement('div');
  wrap.setAttribute('class', 'admin-version-diff');
  wrap.setAttribute('data-testid', 'admin-version-diff');
  wrap.setAttribute('aria-label', t('admin.llm.versions.diff_label'));
  const lines = lineDiff(viewedBody, currentBody);
  if (lines.every((l) => l.op === 'eq')) {
    const same = document.createElement('p');
    same.setAttribute('class', 'admin-version-diff-same');
    same.textContent = t('admin.llm.versions.diff_identical');
    wrap.appendChild(same);
    return wrap;
  }
  const pre = document.createElement('pre');
  pre.setAttribute('class', 'admin-version-diff-body');
  for (const line of lines) {
    const row = document.createElement('span');
    row.setAttribute('class', `admin-version-diff-line admin-version-diff-${line.op}`);
    // Non-colour cue: an explicit +/−/space marker prefixes every line.
    const marker = line.op === 'add' ? '+ ' : line.op === 'del' ? '− ' : '  ';
    row.textContent = `${marker}${line.text}\n`;
    pre.appendChild(row);
  }
  wrap.appendChild(pre);
  return wrap;
}

/**
 * The version-history panel: a template selector → its append-only history
 * (most-recent-first, version #, relative time, author, a "current" badge with
 * a non-colour ✓ cue). Selecting a version shows its body + a line-level diff
 * vs the current version, and offers an inline two-step revert. After a revert
 * the editor list + history both refresh.
 */
function mountVersionHistory(root: Element): () => void {
  const container = root.querySelector('[data-testid="admin-versions-panel"]');
  if (!container) return noopDisposer;
  const statusEl = container.querySelector('[data-versions-status]')!;
  const select = container.querySelector<HTMLSelectElement>('[data-versions-select]')!;
  const listEl = container.querySelector('[data-versions-list]')!;
  const detailEl = container.querySelector('[data-versions-detail]')!;
  let cancelled = false;

  // Refresh the editor list too (after a revert the live body changed).
  const reloadEditors = (): void => {
    const editorContainer = root.querySelector('[data-testid="admin-templates-panel"]');
    const editorStatus = editorContainer?.querySelector('[data-templates-status]');
    const editorList = editorContainer?.querySelector('[data-templates-list]');
    if (!editorStatus || !editorList) return;
    void (async (): Promise<void> => {
      const res = await listTemplates();
      if (cancelled || !res.ok) return;
      clear(editorList);
      for (const tpl of res.value.templates) {
        editorList.appendChild(renderTemplateEditor(tpl, () => void load(select.value)));
      }
    })();
  };

  const renderHistory = (versions: TemplateVersion[]): void => {
    clear(listEl);
    clear(detailEl);
    if (versions.length === 0) {
      setStatus(statusEl, t('admin.llm.versions.empty'));
      return;
    }
    setStatus(statusEl, '');
    const ul = document.createElement('ul');
    ul.setAttribute('class', 'admin-version-list');
    ul.setAttribute('data-testid', 'admin-version-list');
    const current = versions.find((v) => v.is_current) ?? null;

    const showDetail = (v: TemplateVersion): void => {
      clear(detailEl);
      for (const btn of ul.querySelectorAll<HTMLElement>('[data-version-row]')) {
        btn.setAttribute('aria-current', btn.dataset['version'] === String(v.version) ? 'true' : 'false');
      }

      const detailHead = document.createElement('h4');
      detailHead.setAttribute('class', 'admin-version-detail-head');
      detailHead.textContent = t('admin.llm.versions.detail_heading', { version: String(v.version) });
      detailEl.appendChild(detailHead);

      // The version body.
      const bodyLabel = document.createElement('p');
      bodyLabel.setAttribute('class', 'admin-version-detail-label');
      bodyLabel.textContent = t('admin.llm.versions.body_label');
      detailEl.appendChild(bodyLabel);
      const bodyPre = document.createElement('pre');
      bodyPre.setAttribute('class', 'admin-version-body');
      bodyPre.setAttribute('data-testid', 'admin-version-body');
      bodyPre.textContent = v.body;
      detailEl.appendChild(bodyPre);

      // Diff vs current.
      const diffLabel = document.createElement('p');
      diffLabel.setAttribute('class', 'admin-version-detail-label');
      diffLabel.textContent = current
        ? t('admin.llm.versions.diff_heading', { version: String(current.version) })
        : t('admin.llm.versions.diff_heading_default');
      detailEl.appendChild(diffLabel);
      detailEl.appendChild(renderDiff(v.body, current ? current.body : ''));

      // Revert action — hidden for the version that is already current.
      const feedback = document.createElement('span');
      feedback.setAttribute('class', 'admin-version-feedback');
      feedback.setAttribute('role', 'status');
      feedback.setAttribute('aria-live', 'polite');
      if (!v.is_current) {
        const actions = document.createElement('div');
        actions.setAttribute('class', 'admin-version-actions');
        const revertBtn = document.createElement('button');
        revertBtn.setAttribute('type', 'button');
        revertBtn.setAttribute('class', 'admin-version-revert');
        revertBtn.setAttribute('data-testid', 'admin-version-revert');
        revertBtn.textContent = t('admin.llm.versions.revert', { version: String(v.version) });

        const confirmBtn = document.createElement('button');
        confirmBtn.setAttribute('type', 'button');
        confirmBtn.setAttribute('class', 'admin-version-revert-confirm');
        confirmBtn.setAttribute('data-testid', 'admin-version-revert-confirm');
        confirmBtn.textContent = t('admin.llm.versions.revert_confirm');
        confirmBtn.hidden = true;
        const cancelBtn = document.createElement('button');
        cancelBtn.setAttribute('type', 'button');
        cancelBtn.setAttribute('class', 'admin-version-revert-cancel');
        cancelBtn.textContent = t('admin.llm.versions.revert_cancel');
        cancelBtn.hidden = true;

        revertBtn.addEventListener('click', () => {
          revertBtn.hidden = true;
          confirmBtn.hidden = false;
          cancelBtn.hidden = false;
          confirmBtn.focus();
        });
        cancelBtn.addEventListener('click', () => {
          confirmBtn.hidden = true;
          cancelBtn.hidden = true;
          revertBtn.hidden = false;
          revertBtn.focus();
        });
        confirmBtn.addEventListener('click', () => {
          feedback.textContent = '';
          void (async (): Promise<void> => {
            confirmBtn.disabled = true;
            cancelBtn.disabled = true;
            const res = await revertTemplate(select.value, v.version);
            if (cancelled) return;
            if (!res.ok) {
              confirmBtn.disabled = false;
              cancelBtn.disabled = false;
              feedback.textContent = t('admin.llm.versions.revert_error', {
                error: res.kind === 'error' ? res.error : res.kind,
              });
              return;
            }
            if (res.value.missing_required.length > 0) {
              feedback.textContent = t('admin.llm.templates.missing_required', {
                tokens: res.value.missing_required.join(' '),
              });
            }
            reloadEditors();
            void load(select.value);
          })();
        });
        actions.appendChild(revertBtn);
        actions.appendChild(confirmBtn);
        actions.appendChild(cancelBtn);
        detailEl.appendChild(actions);
      }
      detailEl.appendChild(feedback);
    };

    for (const v of versions) {
      const li = document.createElement('li');
      li.setAttribute('class', 'admin-version-item');
      const btn = document.createElement('button');
      btn.setAttribute('type', 'button');
      btn.setAttribute('class', 'admin-version-row');
      btn.setAttribute('data-version-row', '');
      btn.setAttribute('data-version', String(v.version));
      btn.setAttribute('data-testid', `admin-version-row-${v.version}`);
      btn.setAttribute('aria-current', 'false');

      const num = document.createElement('span');
      num.setAttribute('class', 'admin-version-num');
      num.textContent = t('admin.llm.versions.version_n', { version: String(v.version) });
      btn.appendChild(num);

      const when = document.createElement('span');
      when.setAttribute('class', 'admin-version-when');
      when.setAttribute('title', formatAt(v.created_at));
      when.textContent = formatRelative(v.created_at);
      btn.appendChild(when);

      if (v.created_by) {
        const who = document.createElement('span');
        who.setAttribute('class', 'admin-version-author');
        who.textContent = t('admin.llm.versions.author', { author: v.created_by });
        btn.appendChild(who);
      }

      if (v.is_current) {
        const badge = document.createElement('span');
        badge.setAttribute('class', 'admin-version-current-badge');
        badge.setAttribute('data-testid', `admin-version-current-${v.version}`);
        // Non-colour cue: a ✓ glyph + label, not colour alone.
        badge.textContent = t('admin.llm.versions.current_badge');
        btn.appendChild(badge);
      }

      btn.addEventListener('click', () => showDetail(v));
      li.appendChild(btn);
      ul.appendChild(li);
    }
    listEl.appendChild(ul);
    // Auto-select the current version so the panel isn't empty on load.
    const initial = current ?? versions[0]!;
    showDetail(initial);
  };

  const load = async (key: string): Promise<void> => {
    setStatus(statusEl, t('admin.llm.loading'));
    clear(listEl);
    clear(detailEl);
    const res = await listTemplateVersions(key);
    if (cancelled) return;
    if (maybeRenderTopLevelOverlay(container, res)) return;
    if (!res.ok) {
      setStatus(statusEl, t('admin.llm.error.generic'));
      return;
    }
    renderHistory(res.value.versions);
  };

  const onChange = (): void => void load(select.value);
  select.addEventListener('change', onChange);
  void load(select.value);
  return () => {
    cancelled = true;
    select.removeEventListener('change', onChange);
  };
}

// ──────────────────────────── analytics dashboard (#165 increment 3) ─

/** Format micro-USD as a localized dollar string (e.g. $0.0123). */
function formatMicroUsd(micro: number): string {
  const usd = micro / 1_000_000;
  const locale =
    typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en';
  // Show enough fractional digits to surface sub-cent generation costs.
  const digits = usd !== 0 && Math.abs(usd) < 0.01 ? 6 : usd < 1 ? 4 : 2;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: digits,
    }).format(usd);
  } catch {
    return `$${usd.toFixed(digits)}`;
  }
}

/** Format an integer count with locale grouping. */
function formatCount(n: number): string {
  const locale =
    typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en';
  try {
    return new Intl.NumberFormat(locale).format(n);
  } catch {
    return String(n);
  }
}

/** A summary card: label + big value + optional sub-line. */
function analyticsCard(label: string, value: string, sub: string | null, testid: string): Element {
  const card = document.createElement('div');
  card.setAttribute('class', 'admin-analytics-card');
  card.setAttribute('data-testid', testid);
  const labelEl = document.createElement('span');
  labelEl.setAttribute('class', 'admin-analytics-card-label');
  labelEl.textContent = label;
  card.appendChild(labelEl);
  const valueEl = document.createElement('span');
  valueEl.setAttribute('class', 'admin-analytics-card-value');
  valueEl.textContent = value;
  card.appendChild(valueEl);
  if (sub !== null) {
    const subEl = document.createElement('span');
    subEl.setAttribute('class', 'admin-analytics-card-sub');
    subEl.textContent = sub;
    card.appendChild(subEl);
  }
  return card;
}

/**
 * Build a data table. `columns` declares header text + an optional CSS class
 * (e.g. `mono`/`num` for tabular-nums / monospace ids). `cellClass` per column
 * lands on each `<td>` so costs/ids align (#153 table treatment).
 */
function analyticsTable(
  testid: string,
  columns: { label: string; cellClass?: string }[],
  rows: { cells: string[]; key: string }[],
): Element {
  const table = document.createElement('table');
  table.setAttribute('class', 'admin-analytics-table');
  table.setAttribute('data-testid', testid);
  const thead = document.createElement('thead');
  const headRow = document.createElement('tr');
  for (const col of columns) {
    const th = document.createElement('th');
    th.setAttribute('scope', 'col');
    if (col.cellClass) th.setAttribute('class', col.cellClass);
    th.textContent = col.label;
    headRow.appendChild(th);
  }
  thead.appendChild(headRow);
  table.appendChild(thead);
  const tbody = document.createElement('tbody');
  for (const row of rows) {
    const tr = document.createElement('tr');
    tr.setAttribute('data-row-key', row.key);
    row.cells.forEach((cell, i) => {
      const td = document.createElement('td');
      const cls = columns[i]?.cellClass;
      if (cls) td.setAttribute('class', cls);
      td.textContent = cell;
      tr.appendChild(td);
    });
    tbody.appendChild(tr);
  }
  table.appendChild(tbody);
  return table;
}

/** A labelled subsection wrapper with an h3. */
function analyticsSection(headingText: string, testid: string, child: Element): Element {
  const wrap = document.createElement('section');
  wrap.setAttribute('class', 'admin-analytics-section');
  wrap.setAttribute('data-testid', testid);
  const h = document.createElement('h3');
  h.setAttribute('class', 'admin-analytics-subheading');
  h.textContent = headingText;
  wrap.appendChild(h);
  wrap.appendChild(child);
  return wrap;
}

function renderAnalytics(bodyEl: Element, data: Analytics): void {
  clear(bodyEl);

  // Empty window — single empty state, no tables.
  if (data.total_trace_count === 0) {
    const empty = document.createElement('p');
    empty.setAttribute('class', 'admin-panel-body admin-analytics-empty');
    empty.setAttribute('data-testid', 'admin-analytics-empty');
    empty.setAttribute('role', 'status');
    empty.textContent = t('admin.llm.analytics.empty');
    bodyEl.appendChild(empty);
    return;
  }

  // ── Summary cards ──
  const cards = document.createElement('div');
  cards.setAttribute('class', 'admin-analytics-cards');
  cards.setAttribute('data-testid', 'admin-analytics-cards');
  cards.appendChild(
    analyticsCard(
      t('admin.llm.analytics.card_spend'),
      formatMicroUsd(data.total_cost_micro_usd),
      null,
      'admin-analytics-card-spend',
    ),
  );
  cards.appendChild(
    analyticsCard(
      t('admin.llm.analytics.card_traces'),
      formatCount(data.total_trace_count),
      null,
      'admin-analytics-card-traces',
    ),
  );
  const latVal =
    data.latency.avg_ms === null
      ? t('admin.llm.analytics.no_latency')
      : t('admin.llm.analytics.ms', { n: formatCount(data.latency.avg_ms) });
  const latSub =
    data.latency.p50_ms === null
      ? null
      : t('admin.llm.analytics.card_latency_sub', {
          p50: t('admin.llm.analytics.ms', { n: formatCount(data.latency.p50_ms) }),
        });
  cards.appendChild(
    analyticsCard(
      t('admin.llm.analytics.card_latency'),
      latVal,
      latSub,
      'admin-analytics-card-latency',
    ),
  );
  bodyEl.appendChild(cards);

  // ── Spend by function ──
  bodyEl.appendChild(
    analyticsSection(
      t('admin.llm.analytics.by_function_heading'),
      'admin-analytics-by-function',
      analyticsTable(
        'admin-analytics-function-table',
        [
          { label: t('admin.llm.analytics.col_function'), cellClass: 'mono' },
          { label: t('admin.llm.analytics.col_traces'), cellClass: 'num' },
          { label: t('admin.llm.analytics.col_cost'), cellClass: 'num mono' },
        ],
        data.by_function.map((f) => ({
          key: f.function_key,
          cells: [f.function_key, formatCount(f.trace_count), formatMicroUsd(f.cost_micro_usd)],
        })),
      ),
    ),
  );

  // ── Spend by model ──
  bodyEl.appendChild(
    analyticsSection(
      t('admin.llm.analytics.by_model_heading'),
      'admin-analytics-by-model',
      analyticsTable(
        'admin-analytics-model-table',
        [
          { label: t('admin.llm.analytics.col_model'), cellClass: 'mono' },
          { label: t('admin.llm.analytics.col_calls'), cellClass: 'num' },
          { label: t('admin.llm.analytics.col_tokens'), cellClass: 'num' },
          { label: t('admin.llm.analytics.col_cost'), cellClass: 'num mono' },
        ],
        data.by_model.map((m) => ({
          key: m.model_key,
          cells: [
            m.model_key,
            formatCount(m.call_count),
            t('admin.llm.analytics.tokens', {
              input: formatCount(m.input_tokens),
              output: formatCount(m.output_tokens),
            }),
            formatMicroUsd(m.cost_micro_usd),
          ],
        })),
      ),
    ),
  );

  // ── Usage by prompt version ──
  bodyEl.appendChild(
    analyticsSection(
      t('admin.llm.analytics.by_prompt_heading'),
      'admin-analytics-by-prompt',
      analyticsTable(
        'admin-analytics-prompt-table',
        [
          { label: t('admin.llm.analytics.col_function'), cellClass: 'mono' },
          { label: t('admin.llm.analytics.col_prompt'), cellClass: 'mono' },
          { label: t('admin.llm.analytics.col_version'), cellClass: 'num mono' },
          { label: t('admin.llm.analytics.col_uses'), cellClass: 'num' },
          { label: t('admin.llm.analytics.col_cost'), cellClass: 'num mono' },
        ],
        data.by_prompt_version.map((p) => ({
          key: `${p.prompt_template_key}@${p.prompt_template_version}`,
          cells: [
            p.function_key,
            p.prompt_template_key,
            `v${p.prompt_template_version}`,
            formatCount(p.span_count),
            formatMicroUsd(p.cost_micro_usd),
          ],
        })),
      ),
    ),
  );

  // ── Tool-call frequency ──
  const toolsChild: Element =
    data.tool_calls.length === 0
      ? (() => {
          const p = document.createElement('p');
          p.setAttribute('class', 'admin-panel-body');
          p.textContent = t('admin.llm.analytics.tools_empty');
          return p;
        })()
      : analyticsTable(
          'admin-analytics-tools-table',
          [
            { label: t('admin.llm.analytics.col_tool'), cellClass: 'mono' },
            { label: t('admin.llm.analytics.col_calls'), cellClass: 'num' },
          ],
          data.tool_calls.map((tc) => ({
            key: tc.tool_name,
            cells: [tc.tool_name, formatCount(tc.call_count)],
          })),
        );
  bodyEl.appendChild(
    analyticsSection(t('admin.llm.analytics.tools_heading'), 'admin-analytics-tools', toolsChild),
  );

  // ── Acceptance rate — deferred (no draft-outcome→trace link). ──
  if (!data.acceptance_tracked) {
    const note = document.createElement('p');
    note.setAttribute('class', 'admin-panel-body admin-analytics-untracked');
    note.setAttribute('role', 'note');
    note.textContent = t('admin.llm.analytics.acceptance_untracked');
    bodyEl.appendChild(
      analyticsSection(
        t('admin.llm.analytics.acceptance_heading'),
        'admin-analytics-acceptance',
        note,
      ),
    );
  }
}

function mountAnalytics(root: Element): () => void {
  const container = root.querySelector('[data-testid="admin-analytics-panel"]');
  if (!container) return noopDisposer;
  const statusEl = container.querySelector('[data-analytics-status]')!;
  const select = container.querySelector<HTMLSelectElement>('[data-analytics-window]')!;
  const bodyEl = container.querySelector('[data-analytics-body]')!;
  let cancelled = false;

  const load = (window: AnalyticsWindow): void => {
    setStatus(statusEl, t('admin.llm.loading'));
    clear(bodyEl);
    void (async (): Promise<void> => {
      const res = await getAnalytics(window);
      if (cancelled) return;
      if (maybeRenderTopLevelOverlay(container, res)) return;
      if (!res.ok) {
        setStatus(statusEl, t('admin.llm.error.generic'));
        return;
      }
      setStatus(statusEl, '');
      renderAnalytics(bodyEl, res.value);
    })();
  };

  const onChange = (): void => load(select.value as AnalyticsWindow);
  select.addEventListener('change', onChange);
  load((select.value as AnalyticsWindow) || '30d');

  return () => {
    cancelled = true;
    select.removeEventListener('change', onChange);
  };
}

// ──────────────────────── cost-control limits (#216/#217/#218) ─

/** A labelled numeric input that commits its (validated) value on change. */
function limitsNumberField(
  id: string,
  labelText: string,
  helpText: string,
  value: number,
  opts: { min: number; step?: number; testid: string },
  commit: (n: number) => void,
): Element {
  const input = document.createElement('input');
  input.setAttribute('type', 'number');
  input.setAttribute('id', id);
  input.setAttribute('min', String(opts.min));
  if (opts.step) input.setAttribute('step', String(opts.step));
  input.setAttribute('data-testid', opts.testid);
  input.setAttribute('class', 'admin-limits-input');
  input.value = String(value);
  input.addEventListener('change', () => {
    const n = Number(input.value);
    if (!Number.isFinite(n) || n < opts.min) {
      input.value = String(value); // reject — restore last good
      return;
    }
    commit(n);
  });
  return genField(labelText, helpText, input);
}

function renderLimits(
  bodyEl: Element,
  statusEl: Element,
  settings: LlmSettings,
  onPatched: (next: LlmSettings) => void,
): void {
  clear(bodyEl);
  let current = settings;

  const save = (patch: Partial<LlmSettings>): void => {
    setStatus(statusEl, t('admin.llm.saving'));
    void (async (): Promise<void> => {
      const res = await putLlmSettings(patch);
      if (!res.ok) {
        setStatus(statusEl, t('admin.llm.error.generic'));
        return;
      }
      current = res.value;
      onPatched(current);
      setStatus(statusEl, t('admin.llm.limits.saved'));
    })();
  };

  // ── Account-wide kill switch (#218) — the one-click stop, rendered first ──
  const killWrap = document.createElement('div');
  killWrap.setAttribute('class', 'admin-killswitch');
  killWrap.setAttribute('data-testid', 'admin-killswitch');
  const killHeading = document.createElement('h3');
  killHeading.setAttribute('class', 'admin-gen-heading');
  killHeading.textContent = t('admin.llm.limits.kill.title');
  killWrap.appendChild(killHeading);
  const killHelp = document.createElement('p');
  killHelp.setAttribute('class', 'admin-gen-body');
  killHelp.textContent = t('admin.llm.limits.kill.help');
  killWrap.appendChild(killHelp);

  const killStatus = document.createElement('div');
  killStatus.setAttribute('role', 'status');
  killStatus.setAttribute('aria-live', 'polite');
  killStatus.setAttribute('data-testid', 'admin-killswitch-status');
  const killBtn = button({
    variant: 'ghost',
    type: 'button',
    testid: 'admin-killswitch-toggle',
  }) as unknown as Element;

  const paintKill = (engaged: boolean): void => {
    killStatus.setAttribute(
      'class',
      engaged ? 'admin-killswitch-status is-engaged' : 'admin-killswitch-status is-clear',
    );
    killStatus.textContent = engaged
      ? t('admin.llm.limits.kill.engaged')
      : t('admin.llm.limits.kill.clear');
    // Button offers the OPPOSITE action.
    const btnEl = killBtn as HTMLButtonElement;
    btnEl.textContent = engaged
      ? t('admin.llm.limits.kill.action_disengage')
      : t('admin.llm.limits.kill.action_engage');
    btnEl.setAttribute('data-engaged', engaged ? 'true' : 'false');
  };
  paintKill(current.kill_switch);
  (killBtn as HTMLButtonElement).addEventListener('click', () => {
    const next = !current.kill_switch;
    setStatus(statusEl, t('admin.llm.saving'));
    void (async (): Promise<void> => {
      const res = await setKillSwitch(next);
      if (!res.ok) {
        setStatus(statusEl, t('admin.llm.error.generic'));
        return;
      }
      current = res.value;
      onPatched(current);
      paintKill(current.kill_switch);
      setStatus(statusEl, t('admin.llm.limits.saved'));
    })();
  });
  killWrap.appendChild(killStatus);
  killWrap.appendChild(killBtn);
  bodyEl.appendChild(killWrap);

  // ── Budget (#217) — cap shown in USD; stored as micro-USD ──
  const budgetSection = document.createElement('section');
  budgetSection.setAttribute('class', 'admin-gen-settings');
  const budgetHeading = document.createElement('h3');
  budgetHeading.setAttribute('class', 'admin-gen-heading');
  budgetHeading.textContent = t('admin.llm.limits.budget.title');
  budgetSection.appendChild(budgetHeading);
  budgetSection.appendChild(
    limitsNumberField(
      'admin-limits-budget-usd',
      t('admin.llm.limits.budget.cap_label'),
      t('admin.llm.limits.budget.cap_help'),
      Math.round(current.budget_micro_usd / 1_000_000),
      { min: 0, step: 1, testid: 'admin-limits-budget-usd' },
      (usd) => save({ budget_micro_usd: Math.round(usd * 1_000_000) }),
    ),
  );

  // Unlimited-allowed gate (admin-gated toggle, #216 §2).
  {
    const cb = document.createElement('input');
    cb.setAttribute('type', 'checkbox');
    cb.setAttribute('id', 'admin-limits-unlimited');
    cb.setAttribute('data-testid', 'admin-limits-unlimited');
    cb.checked = current.unlimited_allowed;
    cb.addEventListener('change', () => save({ unlimited_allowed: cb.checked }));
    budgetSection.appendChild(
      genField(t('admin.llm.limits.budget.unlimited_label'), t('admin.llm.limits.budget.unlimited_help'), cb),
    );
  }

  // Soft-warning thresholds (50/80/95). One input per threshold.
  const warnIds = ['admin-limits-warn-1', 'admin-limits-warn-2', 'admin-limits-warn-3'];
  current.warn_pct.forEach((pct, i) => {
    budgetSection.appendChild(
      limitsNumberField(
        warnIds[i]!,
        t(`admin.llm.limits.budget.warn_label.${i}`),
        i === 0 ? t('admin.llm.limits.budget.warn_help') : '',
        pct,
        { min: 1, step: 1, testid: `admin-limits-warn-${i + 1}` },
        (n) => {
          const warn = [...current.warn_pct] as [number, number, number];
          warn[i] = n;
          save({ warn_pct: warn });
        },
      ),
    );
  });
  bodyEl.appendChild(budgetSection);

  // ── Rate limits (#217) — the three windows ──
  const rateSection = document.createElement('section');
  rateSection.setAttribute('class', 'admin-gen-settings');
  const rateHeading = document.createElement('h3');
  rateHeading.setAttribute('class', 'admin-gen-heading');
  rateHeading.textContent = t('admin.llm.limits.rate.title');
  rateSection.appendChild(rateHeading);
  const rateBody = document.createElement('p');
  rateBody.setAttribute('class', 'admin-gen-body');
  rateBody.textContent = t('admin.llm.limits.rate.help');
  rateSection.appendChild(rateBody);
  rateSection.appendChild(
    limitsNumberField(
      'admin-limits-rate-minute',
      t('admin.llm.limits.rate.per_minute'),
      '',
      current.rate_per_minute,
      { min: 1, step: 1, testid: 'admin-limits-rate-minute' },
      (n) => save({ rate_per_minute: n }),
    ),
  );
  rateSection.appendChild(
    limitsNumberField(
      'admin-limits-rate-hour',
      t('admin.llm.limits.rate.per_hour'),
      '',
      current.rate_per_hour,
      { min: 1, step: 1, testid: 'admin-limits-rate-hour' },
      (n) => save({ rate_per_hour: n }),
    ),
  );
  rateSection.appendChild(
    limitsNumberField(
      'admin-limits-rate-day',
      t('admin.llm.limits.rate.per_day'),
      '',
      current.rate_per_day,
      { min: 1, step: 1, testid: 'admin-limits-rate-day' },
      (n) => save({ rate_per_day: n }),
    ),
  );
  bodyEl.appendChild(rateSection);
}

function mountLimits(root: Element): () => void {
  const container = root.querySelector('[data-testid="admin-limits-panel"]');
  if (!container) return noopDisposer;
  const statusEl = container.querySelector('[data-limits-status]')!;
  const bodyEl = container.querySelector('[data-limits-body]')!;
  setStatus(statusEl, t('admin.llm.loading'));
  let cancelled = false;
  void (async (): Promise<void> => {
    const res = await getLlmSettings();
    if (cancelled) return;
    if (maybeRenderTopLevelOverlay(container, res)) return;
    if (!res.ok) {
      setStatus(statusEl, t('admin.llm.error.generic'));
      return;
    }
    setStatus(statusEl, '');
    renderLimits(bodyEl, statusEl, res.value, () => undefined);
  })();
  return () => {
    cancelled = true;
  };
}

// ─────────────────────────────────────────────────────────── audit ─

function mountAudit(root: Element): () => void {
  const container = root.querySelector('[data-testid="admin-audit-panel"]');
  if (!container) return noopDisposer;
  const statusEl = container.querySelector('[data-audit-status]')!;
  const listEl = container.querySelector('[data-audit-list]')!;
  setStatus(statusEl, t('admin.llm.loading'));
  let cancelled = false;
  void (async (): Promise<void> => {
    const res = await listAudit(100);
    if (cancelled) return;
    if (maybeRenderTopLevelOverlay(container, res)) return;
    if (!res.ok) {
      setStatus(statusEl, t('admin.llm.error.generic'));
      return;
    }
    const entries = res.value.entries;
    if (entries.length === 0) {
      setStatus(statusEl, t('admin.llm.audit.empty'));
      return;
    }
    setStatus(statusEl, '');
    const tableEl = document.createElement('table');
    tableEl.setAttribute('class', 'admin-audit-table');
    tableEl.setAttribute('data-testid', 'admin-audit-table');
    const caption = document.createElement('caption');
    caption.setAttribute('class', 'sr-only');
    caption.textContent = t('admin.llm.audit.title');
    tableEl.appendChild(caption);
    const theadEl = document.createElement('thead');
    const headRow = document.createElement('tr');
    for (const heading of [
      t('admin.llm.audit.at'),
      t('admin.llm.audit.admin'),
      t('admin.llm.audit.action'),
      t('admin.llm.audit.target'),
    ]) {
      const thEl = document.createElement('th');
      thEl.setAttribute('scope', 'col');
      thEl.textContent = heading;
      headRow.appendChild(thEl);
    }
    theadEl.appendChild(headRow);
    tableEl.appendChild(theadEl);
    const tbodyEl = document.createElement('tbody');
    for (const e of entries) {
      const rowEl = document.createElement('tr');
      rowEl.setAttribute('data-testid', `admin-audit-${e.id}`);
      const tdAt = document.createElement('td');
      tdAt.textContent = formatAt(e.at);
      rowEl.appendChild(tdAt);
      const tdAdmin = document.createElement('td');
      tdAdmin.textContent = e.admin_user_id;
      rowEl.appendChild(tdAdmin);
      const tdAction = document.createElement('td');
      tdAction.textContent = e.action;
      rowEl.appendChild(tdAction);
      const tdTarget = document.createElement('td');
      tdTarget.textContent = e.provider_id ?? e.key_id ?? '';
      rowEl.appendChild(tdTarget);
      tbodyEl.appendChild(rowEl);
    }
    tableEl.appendChild(tbodyEl);
    listEl.appendChild(tableEl);
  })();
  return () => {
    cancelled = true;
  };
}

// ────────────────────────────────────────────────────────── tab strip ─

/** Workflow tabs. LLui owns the tab state/ARIA/keyboard contract; the admin
 * route keeps only the panel visibility + deep-link side effect. */
const ADMIN_TABS = ['catalog', 'bindings', 'prompts', 'limits', 'analytics'] as const;
type AdminTab = (typeof ADMIN_TABS)[number];

const ADMIN_TAB_LABEL_KEY: Record<AdminTab, string> = {
  catalog: 'admin.llm.tabs.catalog',
  bindings: 'admin.llm.tabs.bindings',
  prompts: 'admin.llm.tabs.prompts',
  limits: 'admin.llm.tabs.limits',
  analytics: 'admin.llm.tabs.analytics',
};

const DEFAULT_ADMIN_TAB: AdminTab = 'catalog';

function isAdminTab(value: string | null | undefined): value is AdminTab {
  return value != null && (ADMIN_TABS as readonly string[]).includes(value);
}

/**
 * Initial tab — a `?tab=` query wins (deep-linkable + lets e2e land directly
 * on a non-default tab without a click), otherwise the default. Read in a
 * try/catch so a non-browser context can't throw at module evaluation.
 */
function initialAdminTab(): AdminTab {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('tab');
    if (isAdminTab(fromUrl)) return fromUrl;
  } catch {
    // No window / malformed URL — fall through to the default.
  }
  return DEFAULT_ADMIN_TAB;
}

function tabIdFor(tab: AdminTab): string {
  return `admin-llm-${tab}-tab`;
}

function panelGroupIdFor(tab: AdminTab): string {
  return `admin-llm-${tab}-panels`;
}

/** Show one panel group and reflect it in the deep-link URL. */
function activateAdminPanel(root: Element, tab: AdminTab): void {
  for (const t of ADMIN_TABS) {
    const group = root.querySelector<HTMLElement>(`#${panelGroupIdFor(t)}`);
    if (group) group.hidden = t !== tab;
  }
  // Reflect the active tab in the URL (deep-linkable; survives reload). Use
  // replaceState so tab switches don't stack history entries.
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get('tab') !== tab) {
      url.searchParams.set('tab', tab);
      window.history.replaceState(window.history.state, '', url.toString());
    }
  } catch {
    // Non-browser / locked-down history — visibility toggle already applied.
  }
}

/** LLui tab strip; admin data panels remain mounted and are merely hidden. */
function tabStrip(initialTab: AdminTab): Mountable {
  return tabs({
    idBase: 'admin-llm',
    className: 'admin-llm-tabs',
    testid: 'admin-llm-tabs',
    ariaLabel: t('admin.llm.tabs.list_label'),
    items: ADMIN_TABS.map((tab) => ({
      value: tab,
      label: t(ADMIN_TAB_LABEL_KEY[tab]),
      active: tab === initialTab,
      panelId: panelGroupIdFor(tab),
    })),
    onActivate: (value) => {
      if (!isAdminTab(value)) return;
      const root = document.querySelector('[data-admin-root]');
      if (root) activateAdminPanel(root, value);
    },
  });
}

/** Wrap a tab's panels in a `role=tabpanel` group, hidden unless active. */
function tabPanelGroup(tab: AdminTab, panels: Mountable[], initialTab: AdminTab): Mountable {
  return div(
    {
      id: panelGroupIdFor(tab),
      class: 'admin-llm-tabpanel',
      role: 'tabpanel',
      'aria-labelledby': tabIdFor(tab),
      'data-tab-panels': tab,
      'data-testid': `admin-llm-panels-${tab}`,
      ...(tab !== initialTab ? { hidden: '' } : {}),
    },
    panels,
  );
}

// ─────────────────────────────────────────────────────────────── view ─

export function viewAdminLlm<Model, Msg>(_state: Signal<Model>, _send: Send<Msg>): Mountable {
  // Reset module-scope caches on every fresh mount of the route so the
  // panel state is route-local rather than tab-local (a sign-out + sign-
  // in by a different admin should re-fetch).
  providersCache.providers = [];
  providersCache.selectedProviderId = null;
  modelsCache.providerId = null;
  modelsCache.models = [];
  keysCache.providerId = null;
  keysCache.keys = [];
  bindingsCache.bindings = [];
  const initialTab = initialAdminTab();

  // Single mount hook fans out to every panel. Each `mount*()` finds its own
  // anchor inside the route subtree by selector. `onAdminMount` defers the
  // fan-out until `[data-admin-root]` is connected to the document, then runs
  // it once — under the signal runtime the mount callback can fire before the
  // returned subtree is attached, and a synchronous lookup would return null
  // and silently bail (incl. the non-admin not-found overlay; #198/#199).
  // Returning a disposer chain cleans up listeners + in-flight fetches on
  // navigate-away. The returned Mountable MUST be placed in the view tree
  // (first child below) — a discarded mount hook never fires.
  const mountHook = onAdminMount('[data-admin-root]', (root) => {
    const disposers: (() => void)[] = [
      mountProviders(root),
      mountModels(root),
      mountKeys(root),
      mountBindings(root),
      mountTemplates(root),
      mountVersionHistory(root),
      mountLimits(root),
      mountAnalytics(root),
      mountAudit(root),
    ];
    return () => {
      for (const d of disposers) {
        try {
          d();
        } catch {
          // Disposers are best-effort; a thrown disposer must not block the rest.
        }
      }
    };
  });

  return main(
    { role: 'main', class: 'admin-llm-view', 'aria-labelledby': 'admin-llm-heading', 'data-admin-root': '', 'data-testid': 'admin-llm-view' },
    [
      mountHook,
      pageHeader({
        title: text(t('admin.llm.title')),
        titleId: 'admin-llm-heading',
        intro: text(t('admin.llm.intro')),
      }),

      // Workflow tab strip (Catalog · Bindings · Prompts · Analytics) ─
      tabStrip(initialTab),

      // ── Catalog tab ─ providers + models + keys ──────────────────
      tabPanelGroup('catalog', [
      // Providers panel ────────────────────────────────────────────
      section(
        {
          class: 'admin-panel admin-panel-providers',
          'aria-labelledby': 'admin-providers-heading',
          'data-testid': 'admin-providers-panel',
        },
        [
          sectionHeading({ title: text(t('admin.llm.providers.title')), level: 2, id: 'admin-providers-heading' }),
          div(
            {
              id: 'admin-providers-status',
              'data-providers-status': '',
              role: 'status',
              'aria-live': 'polite',
              class: 'admin-panel-status',
            },
            [],
          ),
          div(
            {
              id: 'admin-providers-list',
              'data-providers-list': '',
              'data-testid': 'admin-providers',
            },
            [],
          ),
        ],
      ),

      // Models panel ──────────────────────────────────────────────
      section(
        {
          class: 'admin-panel admin-panel-models',
          'aria-labelledby': 'admin-models-heading',
          'data-testid': 'admin-models-panel',
        },
        [
          sectionHeading({ title: text(t('admin.llm.models.title')), level: 2, id: 'admin-models-heading' }),
          div({ class: 'admin-panel-select', 'data-models-provider-select': '' }, []),
          div(
            {
              id: 'admin-models-status',
              'data-models-status': '',
              role: 'status',
              'aria-live': 'polite',
              class: 'admin-panel-status',
            },
            [],
          ),
          div(
            { id: 'admin-models-list', 'data-models-list': '', 'data-testid': 'admin-models' },
            [],
          ),
        ],
      ),

      // Keys panel ────────────────────────────────────────────────
      section(
        {
          class: 'admin-panel admin-panel-keys',
          'aria-labelledby': 'admin-keys-heading',
          'data-testid': 'admin-keys-panel',
        },
        [
          sectionHeading({ title: text(t('admin.llm.keys.title')), level: 2, id: 'admin-keys-heading' }),
          p({ class: 'admin-llm-warning' }, [text(t('admin.llm.keys.warning'))]),
          div({ class: 'admin-panel-select', 'data-keys-provider-select': '' }, []),
          div(
            {
              id: 'admin-keys-status',
              'data-keys-status': '',
              role: 'status',
              'aria-live': 'polite',
              class: 'admin-panel-status',
            },
            [],
          ),
          div(
            { id: 'admin-keys-list', 'data-keys-list': '', 'data-testid': 'admin-keys' },
            [],
          ),
          sectionHeading({ title: text(t('admin.llm.keys.add_heading')), level: 3 }),
          form(
            {
              class: 'admin-keys-form',
              'data-keys-form': '',
              'data-testid': 'admin-keys-form',
              novalidate: true,
            },
            [
              div({ class: 'admin-form-row' }, [
                label({ for: 'admin-key-label' }, [text(t('admin.llm.keys.label'))]),
                textInput({
                  type: 'text',
                  testid: 'admin-key-label',
                  placeholder: '',
                  attrs: {
                    id: 'admin-key-label',
                    name: 'label',
                    required: true,
                    'aria-describedby': 'admin-key-label-error',
                  },
                }),
                span(
                  {
                    id: 'admin-key-label-error',
                    'data-error-for': 'admin-key-label',
                    class: 'admin-form-error',
                    role: 'alert',
                  },
                  [],
                ),
              ]),
              div({ class: 'admin-form-row' }, [
                label({ for: 'admin-key-plaintext' }, [
                  text(t('admin.llm.keys.plaintext_field')),
                ]),
                textInput({
                  type: 'password',
                  testid: 'admin-key-plaintext',
                  attrs: {
                    id: 'admin-key-plaintext',
                    name: 'plaintext_key',
                    autocomplete: 'off',
                    required: true,
                    'aria-describedby': 'admin-key-plaintext-error',
                  },
                }),
                span(
                  {
                    id: 'admin-key-plaintext-error',
                    'data-error-for': 'admin-key-plaintext',
                    class: 'admin-form-error',
                    role: 'alert',
                  },
                  [],
                ),
              ]),
              div({ class: 'admin-form-actions' }, [
                button({
                  variant: 'primary',
                  type: 'submit',
                  testid: 'admin-key-submit',
                  children: [text(t('admin.llm.keys.submit'))],
                }),
              ]),
              p(
                {
                  class: 'admin-keys-feedback',
                  'data-keys-feedback': '',
                  'data-testid': 'admin-keys-feedback',
                  role: 'status',
                  'aria-live': 'polite',
                },
                [],
              ),
            ],
          ),
        ],
      ),

      ], initialTab),

      // ── Bindings tab ─ function→model matrix + gen settings + estimate ─
      tabPanelGroup('bindings', [
      // Bindings matrix ──────────────────────────────────────────
      section(
        {
          class: 'admin-panel admin-panel-bindings',
          'aria-labelledby': 'admin-bindings-heading',
          'data-testid': 'admin-bindings-panel',
        },
        [
          sectionHeading({ title: text(t('admin.llm.bindings.title')), level: 2, id: 'admin-bindings-heading' }),
          div(
            {
              id: 'admin-bindings-status',
              'data-bindings-status': '',
              role: 'status',
              'aria-live': 'polite',
              class: 'admin-panel-status',
            },
            [],
          ),
          div(
            {
              id: 'admin-bindings-matrix',
              'data-bindings-matrix': '',
              'data-testid': 'admin-bindings',
            },
            [],
          ),
        ],
      ),
      ], initialTab),

      // ── Prompts tab ─ template editor (+ version-history placeholder) ─
      tabPanelGroup('prompts', [
      // Prompt templates ─────────────────────────────────────────
      section(
        {
          class: 'admin-panel admin-panel-templates',
          'aria-labelledby': 'admin-templates-heading',
          'data-testid': 'admin-templates-panel',
        },
        [
          sectionHeading({ title: text(t('admin.llm.templates.title')), level: 2, id: 'admin-templates-heading' }),
          p({ class: 'admin-panel-body' }, [text(t('admin.llm.templates.body'))]),
          div(
            {
              id: 'admin-templates-status',
              'data-templates-status': '',
              role: 'status',
              'aria-live': 'polite',
              class: 'admin-panel-status',
            },
            [],
          ),
          div({ 'data-templates-list': '', 'data-testid': 'admin-templates' }, []),
        ],
      ),
      // Version history ─ browse / diff / revert (increment 2, #165) ──
      section(
        {
          class: 'admin-panel admin-panel-versions',
          'aria-labelledby': 'admin-versions-heading',
          'data-testid': 'admin-versions-panel',
        },
        [
          sectionHeading({ title: text(t('admin.llm.versions.heading')), level: 2, id: 'admin-versions-heading' }),
          p({ class: 'admin-panel-body' }, [text(t('admin.llm.versions.body'))]),
          div({ class: 'admin-versions-controls' }, [
            label(
              { class: 'admin-versions-select-label', for: 'admin-versions-select' },
              [text(t('admin.llm.versions.template_label'))],
            ),
            select(
              {
                id: 'admin-versions-select',
                class: 'admin-versions-select',
                'data-versions-select': '',
                'data-testid': 'admin-versions-select',
              },
              TEMPLATE_KEYS.map((key) => option({ value: key }, [text(key)])),
            ),
          ]),
          div(
            {
              id: 'admin-versions-status',
              'data-versions-status': '',
              role: 'status',
              'aria-live': 'polite',
              class: 'admin-panel-status',
            },
            [],
          ),
          div({ class: 'admin-versions-layout' }, [
            div(
              {
                'data-versions-list': '',
                'data-testid': 'admin-versions',
                class: 'admin-versions-list-col',
                role: 'region',
                'aria-label': t('admin.llm.versions.list_label'),
              },
              [],
            ),
            div(
              {
                'data-versions-detail': '',
                'data-testid': 'admin-versions-detail',
                class: 'admin-versions-detail-col',
                role: 'region',
                'aria-label': t('admin.llm.versions.detail_label'),
              },
              [],
            ),
          ]),
        ],
      ),
      ], initialTab),

      // ── Limits tab ─ cost-control policy (#216/#217/#218) ─
      tabPanelGroup('limits', [
        section(
          {
            class: 'admin-panel admin-panel-limits',
            'aria-labelledby': 'admin-limits-heading',
            'data-testid': 'admin-limits-panel',
          },
          [
            sectionHeading({ title: text(t('admin.llm.limits.heading')), level: 2, id: 'admin-limits-heading' }),
            p({ class: 'admin-panel-body' }, [text(t('admin.llm.limits.intro'))]),
            div(
              {
                'data-limits-status': '',
                role: 'status',
                'aria-live': 'polite',
                class: 'admin-panel-status',
              },
              [],
            ),
            div({ 'data-limits-body': '', 'data-testid': 'admin-limits-body' }, []),
          ],
        ),
      ], initialTab),

      // ── Analytics tab ─ trace-backed spend dashboard + admin audit log ─
      tabPanelGroup('analytics', [
      // Spend & prompt analytics dashboard (#165 increment 3). The window
      // selector + summary cards + tables are populated imperatively by
      // `mountAnalytics`; the static skeleton below is just the landmark
      // heading, the window control, the status node, and the body anchor.
      section(
        {
          class: 'admin-panel admin-panel-analytics',
          'aria-labelledby': 'admin-analytics-heading',
          'data-testid': 'admin-analytics-panel',
        },
        [
          sectionHeading({ title: text(t('admin.llm.analytics.heading')), level: 2, id: 'admin-analytics-heading' }),
          p({ class: 'admin-panel-body' }, [text(t('admin.llm.analytics.intro'))]),
          div({ class: 'admin-analytics-controls' }, [
            label({ for: 'admin-analytics-window', class: 'admin-analytics-window-label' }, [
              text(t('admin.llm.analytics.window_label')),
            ]),
            select(
              {
                id: 'admin-analytics-window',
                'data-analytics-window': '',
                'data-testid': 'admin-analytics-window',
                class: 'admin-analytics-window-select',
              },
              [
                option({ value: '30d' }, [text(t('admin.llm.analytics.window_30d'))]),
                option({ value: '7d' }, [text(t('admin.llm.analytics.window_7d'))]),
                option({ value: 'all' }, [text(t('admin.llm.analytics.window_all'))]),
              ],
            ),
          ]),
          div(
            {
              id: 'admin-analytics-status',
              'data-analytics-status': '',
              role: 'status',
              'aria-live': 'polite',
              class: 'admin-panel-status',
            },
            [],
          ),
          div(
            { 'data-analytics-body': '', 'data-testid': 'admin-analytics-body' },
            [],
          ),
        ],
      ),
      // Audit log ────────────────────────────────────────────────
      section(
        {
          class: 'admin-panel admin-panel-audit',
          'aria-labelledby': 'admin-audit-heading',
          'data-testid': 'admin-audit-panel',
        },
        [
          sectionHeading({ title: text(t('admin.llm.audit.title')), level: 2, id: 'admin-audit-heading' }),
          div(
            {
              id: 'admin-audit-status',
              'data-audit-status': '',
              role: 'status',
              'aria-live': 'polite',
              class: 'admin-panel-status',
            },
            [],
          ),
          div(
            { id: 'admin-audit-log', 'data-audit-list': '', 'data-testid': 'admin-audit' },
            [],
          ),
        ],
      ),
      ], initialTab),
    ],
  );
}
